/**
 * Switch - Toggle Switch Component
 *
 * On/off toggle input. Renders as Box with accessible checkbox underneath.
 *
 * @example
 * <Switch checked={true} onChange={(checked) => {}} />
 * <Switch checked={false} label="Enable notifications" />
 */
import { forwardRef } from "react";
import { Box } from "@/shared/ui/base";
import { Label } from "./Label";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

export interface SwitchProps extends BaseComponentProps {
  /** Checked state */
  checked?: boolean;
  /** Change handler */
  onChange?: (checked: boolean) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Optional label */
  label?: string;
  /** Label position */
  labelPosition?: "left" | "right";
}

/**
 * Switch component
 * Renders as a Box with hidden checkbox and visual toggle
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      checked = false,
      onChange,
      disabled,
      label,
      labelPosition = "right",
      className,
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked);
    };

    const switchElement = (
      <Box className="relative inline-flex items-center">
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only peer"
          {...props}
        />
        <Box
          className={cn(
            "w-11 h-6 rounded-full transition-colors peer-disabled:opacity-50 peer-disabled:cursor-not-allowed",
            checked ? "bg-red-600" : "bg-gray-300",
          )}
        >
          <Box
            className={cn(
              "absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform",
              checked && "translate-x-5",
            )}
          />
        </Box>
      </Box>
    );

    if (!label) {
      return switchElement;
    }

    return (
      <Box className={cn("inline-flex items-center gap-2", className)}>
        {labelPosition === "left" && <Label disabled={disabled}>{label}</Label>}
        {switchElement}
        {labelPosition === "right" && (
          <Label disabled={disabled}>{label}</Label>
        )}
      </Box>
    );
  },
);

Switch.displayName = "Switch";
